// Tron Legacy 3D background — assembles renderer, grid, and bloom
// Uses WebGPURenderer (auto WebGPU → WebGL2 fallback)

import { Scene, PerspectiveCamera, FogExp2, Clock, Frustum, Matrix4, Vector3 } from 'three/webgpu';
import { uniform } from 'three/tsl';
import type { RenderCapabilities } from './detect-capabilities';
import { createRenderer } from './create-renderer';
import { createGrid } from './create-grid';
import { createPostProcessing } from './create-post-processing';
import { createLightColumns } from './create-light-columns';
import { getWaypoint } from './camera-waypoints';

export interface TronScene {
  start: () => void;
  dispose: () => void;
}

export function createTronScene(caps: RenderCapabilities): TronScene {
  const { renderer, dispose: rendererDispose } = createRenderer(caps);

  const scene = new Scene();
  scene.fog = new FogExp2(0x0a0a0f, 0.025);

  const waypoint = getWaypoint();

  const camera = new PerspectiveCamera(waypoint.fov, window.innerWidth / window.innerHeight, 0.1, 200);
  camera.position.set(...waypoint.position);
  camera.lookAt(...waypoint.target);

  // Mouse parallax — disabled on touch/mobile devices
  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
  let mouseX = 0;
  let mouseY = 0;

  function onMouseMove(e: MouseEvent): void {
    // Normalize to [-1, 1] range (center = 0)
    mouseX = (e.clientX / window.innerWidth) * 2 - 1;
    mouseY = (e.clientY / window.innerHeight) * 2 - 1;
  }

  if (!isTouchDevice) {
    window.addEventListener('mousemove', onMouseMove);
  }

  // Shared time uniform — controlled manually for pause/resume
  const uTime = uniform(0.0);

  const grid = createGrid(uTime, caps);
  scene.add(grid.mesh);

  // Frustum check — only spawn ripples for columns visible to the camera
  const frustum = new Frustum();
  const projScreenMatrix = new Matrix4();
  const tempVec = new Vector3();

  function isPointVisible(x: number, y: number, z: number): boolean {
    projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(projScreenMatrix);
    return frustum.containsPoint(tempVec.set(x, y, z));
  }

  // Light columns — vertical cyan pillars at grid intersections
  // Ripples spawn from column positions when they fade out (if in camera view)
  const columns = createLightColumns((x, z, time) => {
    if (isPointVisible(x, 0, z)) {
      grid.spawnRipple(x, z, time);
    }
  });
  scene.add(columns.group);

  // Post-processing — bloom and vignette controlled by quality budget
  const pp = createPostProcessing(renderer, scene, camera, caps);

  // Resize — PostProcessing auto-syncs with renderer
  function onResize(): void {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', onResize);

  const clock = new Clock(false);
  let elapsed = 0;
  let realTime = 0;               // wall-clock time (not accelerated)
  const FADE_DURATION = 2.0;       // canvas opacity fade (CSS)
  const SPEED_RAMP_DURATION = 3.0; // grid speed deceleration duration
  const FADE_SPEED_BOOST = 30.0;   // grid moves 30x faster at start
  const CAMERA_START_Y_OFFSET = 100.0;  // camera starts this much higher

  // Scroll-reactive grid speed: accumulate scroll delta between frames,
  // then smooth it to modulate the time progression rate
  let scrollDelta = 0;
  let smoothScrollSpeed = 0;
  const SCROLL_INFLUENCE_DOWN = 0.048;
  const SCROLL_INFLUENCE_UP = 0.012;
  const SCROLL_DAMPING = 0.9;

  let lastScrollY = window.scrollY;
  function onScroll(): void {
    scrollDelta += window.scrollY - lastScrollY;
    lastScrollY = window.scrollY;
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  // Grid Y shift: rises when scrolling down, lowers when scrolling up, returns to base when idle
  const GRID_BASE_Y = -2;
  const GRID_MAX_LIFT = 18.0;
  const GRID_MAX_DROP = 8.0;
  const GRID_SHIFT_FACTOR = 0.18;
  const GRID_SHIFT_LERP = 0.04;
  let gridY = GRID_BASE_Y;

  // Camera look-at tilt: horizon at top of page, downward at bottom
  const LOOK_HORIZON_Y = 4.0;   // target Y offset when at top (looks toward horizon)
  const LOOK_DOWN_Y = -6.0;     // target Y offset when at bottom (looks down)
  let smoothLookY = waypoint.target[1];

  // Current camera position for smooth lerping (start at waypoint base)
  let camX = waypoint.position[0];
  let camY = waypoint.position[1];
  const LERP_FACTOR = 0.05;

  function hideGridFallback(): void {
    const gridBg = document.querySelector<HTMLElement>('.grid-bg');
    if (gridBg) gridBg.style.display = 'none';
  }

  function animate(): void {
    // Cap delta to avoid jumps after tab switch
    const delta = Math.min(clock.getDelta(), 0.1);

    // Blend scroll velocity and decay it smoothly (asymmetric: stronger when scrolling down)
    const influence = scrollDelta > 0 ? SCROLL_INFLUENCE_DOWN : SCROLL_INFLUENCE_UP;
    smoothScrollSpeed = smoothScrollSpeed * SCROLL_DAMPING + scrollDelta * influence;
    scrollDelta = 0;
    const speedMultiplier = Math.max(0.1, 1.0 + smoothScrollSpeed);

    realTime += delta;

    // Boost grid speed at scene start: decelerates over SPEED_RAMP_DURATION (real time)
    const rampProgress = Math.min(1.0, realTime / SPEED_RAMP_DURATION);
    const fadeBoost = 1.0 + (FADE_SPEED_BOOST - 1.0) * (1.0 - rampProgress);

    elapsed += delta * speedMultiplier * fadeBoost;
    uTime.value = elapsed;
    columns.update(elapsed);

    // Grid shift: scroll down → grid rises (closer), scroll up → grid drops (farther)
    const shift = smoothScrollSpeed * GRID_SHIFT_FACTOR;
    const clampedShift = shift > 0
      ? Math.min(GRID_MAX_LIFT, shift)
      : Math.max(-GRID_MAX_DROP, shift);
    const targetGridY = GRID_BASE_Y + clampedShift;
    gridY += (targetGridY - gridY) * GRID_SHIFT_LERP;
    grid.mesh.position.y = gridY;
    columns.group.position.y = gridY;  // column bottoms aligned with grid plane

    // Camera intro: start higher and descend over the ramp duration
    const introYOffset = CAMERA_START_Y_OFFSET * (1.0 - rampProgress);

    // Target position: waypoint base + sinusoidal drift + mouse parallax + intro offset
    const targetX = waypoint.position[0] + Math.sin(elapsed * 0.2) * 1.5 + mouseX * 2.0;
    const targetY = waypoint.position[1] + Math.sin(elapsed * 0.3) * 0.5 + mouseY * 1.0 + introYOffset;

    // Smooth lerp toward target
    camX += (targetX - camX) * LERP_FACTOR;
    camY += (targetY - camY) * LERP_FACTOR;

    // Micro-oscillation breathing on Y axis
    const breathing = Math.sin(elapsed * 0.1) * 0.015;

    camera.position.x = camX;
    camera.position.y = camY + breathing;

    // Tilt camera based on scroll position: 0 at top → horizon, 1 at bottom → downward
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const scrollRatio = window.scrollY / maxScroll;
    const targetLookY = waypoint.target[1] + LOOK_HORIZON_Y + (LOOK_DOWN_Y - LOOK_HORIZON_Y) * scrollRatio;
    smoothLookY += (targetLookY - smoothLookY) * LERP_FACTOR;
    camera.lookAt(waypoint.target[0], smoothLookY, waypoint.target[2]);

    // Slight banking: camera rolls when drifting sideways
    const lateralVelocity = targetX - camX;
    camera.rotation.z = lateralVelocity * 0.03;

    pp.postProcessing.render();
  }

  function start(): void {
    hideGridFallback();

    // Fade in the canvas
    requestAnimationFrame(() => {
      renderer.domElement.style.opacity = '1';
    });

    if (caps.reducedMotion) {
      // Render a single static frame
      uTime.value = 0;
      pp.postProcessing.render();
      return;
    }

    clock.start();
    renderer.setAnimationLoop(animate);
  }

  function dispose(): void {
    renderer.setAnimationLoop(null);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    if (!isTouchDevice) {
      window.removeEventListener('mousemove', onMouseMove);
    }
    grid.dispose();
    columns.dispose();
    pp.dispose();
    rendererDispose();
  }

  return { start, dispose };
}
